// Locale-aware amount parser for imported bank statement files (CSV/XLSX).
//
// Bank/Excel exports arrive in mixed formats:
//   BR  "1.234,56"      US  "1,234.56"
//   plain "1234.56"     thousands-only "1.500"  (no cents -> means 1500)
//
// The previous implementation assumed "comma present => Brazilian" and stripped
// every dot, which mangled US-formatted numbers ("1,234.56" -> 1.23) and read
// Brazilian thousands without cents as decimals ("1.500" -> 1.5).
//
// Strategy:
//   - When BOTH '.' and ',' are present, the RIGHTMOST one is the decimal
//     separator and the other is a thousands grouping.
//   - When only ONE kind of separator is present, treat it as the decimal
//     point ONLY when it appears once and is followed by 1-2 digits (currency
//     cents). Otherwise it's a thousands grouping (e.g. "1.500" -> 1500).
//   - Currency symbols, spaces, a leading sign and accounting negatives
//     "(1.234,56)" are all handled. Native numbers (xlsx numeric cells) pass
//     through untouched.
//
// NOTE: intentionally tuned for 2-decimal currency (bank statements), so a lone
// separator with 3 digits after it ("1.234") is read as thousands.
//
// Authored as CommonJS so it can be unit-tested directly under Node's built-in
// test runner without a build step; it still imports cleanly into Import.jsx
// via Babel's ESM/CJS interop (`import { parseAmount } from './parseAmount'`).
// ES5-safe body (no optional chaining / nullish coalescing) per build rules.
function parseAmount(value) {
    if (value === null || value === undefined || value === '') return 0;
    // xlsx numeric cells already come through as real numbers.
    if (typeof value === 'number') return isNaN(value) ? 0 : value;

    var str = String(value).trim();
    if (!str) return 0;

    // Accounting negatives: "(1.234,56)" -> -1234.56
    var negative = false;
    if (str.charAt(0) === '(' && str.charAt(str.length - 1) === ')') {
        negative = true;
        str = str.slice(1, -1);
    }

    // Keep only digits, separators and sign.
    str = str.replace(/[^0-9.,-]/g, '');
    if (str.charAt(0) === '-') negative = true;
    str = str.replace(/-/g, '');
    if (!str) return 0;

    var lastDot = str.lastIndexOf('.');
    var lastComma = str.lastIndexOf(',');
    var decimalSep = null;

    if (lastDot > -1 && lastComma > -1) {
        // Both present: the rightmost separator is the decimal point.
        decimalSep = lastDot > lastComma ? '.' : ',';
    } else {
        // Only one kind (or none). Treat it as the decimal point only when it
        // appears once and is followed by 1-2 digits (currency cents);
        // otherwise it's a thousands grouping (e.g. "1.500" -> 1500).
        var sep = lastComma > -1 ? ',' : (lastDot > -1 ? '.' : null);
        if (sep) {
            var parts = str.split(sep);
            var decimals = parts[parts.length - 1].length;
            if (parts.length === 2 && (decimals === 1 || decimals === 2)) {
                decimalSep = sep;
            }
        }
    }

    var normalized;
    if (decimalSep) {
        var thousandsSep = decimalSep === '.' ? ',' : '.';
        normalized = str.split(thousandsSep).join('').replace(decimalSep, '.');
    } else {
        normalized = str.replace(/[.,]/g, '');
    }

    var num = parseFloat(normalized);
    if (isNaN(num)) return 0;
    return negative ? -num : num;
}

module.exports = { parseAmount: parseAmount };
module.exports.default = parseAmount;
